import QueryString from 'qs';

import { ResetPasswordForm } from '../@types/ResetPasswordForm';
import { PaymentMethods } from '../Account/components/Deposit/DepositService';
import { remoteJSON } from './remote';

export const getCurrentUser = async () => {
	const response = (await remoteJSON(
		'/users/current?embed=user_bank_account,limits,bpay_details,payid_details,user_cards',
		undefined,
		['session-time']
	)) as GetCurrentUserResponse & {
		__headers: { 'session-time': string | null | undefined };
	};

	return {
		user: {
			...response.data,
			locale: response.meta,
		},
		sessionTime: response?.__headers['session-time'],
	};
};

export const getSignupConfig = async () => {
	const response = (await remoteJSON('/users/register')) as GetSignupConfigResponse;

	return response;
};

export const updateCurrentUser = async (updates: Partial<PortalUser | { avatar: { image: string | null } }>) => {
	const response = (await remoteJSON('/users/current', {
		method: 'POST',
		body: JSON.stringify(updates),
	})) as GetCurrentUserResponse;

	return response;
};

export const signupNewUser = async (newUser: UserSignup) => {
	const response = (await remoteJSON('/users', {
		method: 'POST',
		headers: { 'Registration-Source': 'Website' },
		body: JSON.stringify(newUser),
	})) as SignupResponse;

	return response;
};

export const resetPassword = async (email: string) => {
	const response = (await remoteJSON('/users/password/request', {
		method: 'POST',
		body: JSON.stringify({ email }),
	})) as ResetPasswordResponse;

	return response;
};

export const updatePassword = async (data: ResetPasswordForm) => {
	return (await remoteJSON('/users/password', {
		method: 'POST',
		body: JSON.stringify(data),
	})) as APIMessageResponse;
};

export const getPendingWithdrawRequests = async (page: string) => {
	const params = QueryString.stringify({
		payment_status_ids: '1,2',
		page,
	});

	const response = (await remoteJSON(`/users/withdraw?${params}`)) as GetPendingWithdrawalsResponse;

	return response;
};

export const uploadBankDocument = async (bankid: number, document: string | ArrayBuffer) => {
	const response = await remoteJSON(`/users/bank/${bankid}`, { method: 'POST', body: document });

	return response;
};

export const deleteBankDocument = async (bankId) => {
	const response = await remoteJSON(`/users/bank/${bankId}`, { method: 'DELETE' });

	return response;
};

export const deleteCreditcard = async (id: number) => {
	const response = (await remoteJSON(`/users/card/${id}`, {
		method: 'DELETE',
	})) as CreditcardDeleteResponse;

	return response;
};

export const selfExclude = async () => {
	const response = (await remoteJSON('/users/self_exclude', { method: 'POST' })) as APIMessageResponse;

	return response;
};

export const closeAccount = async () => {
	const response = (await remoteJSON('/users/close', { method: 'POST' })) as CloseAccountResponse;

	return response;
};

export const takeBreak = async (period: string | null) => {
	const response = (await remoteJSON('/users/break', {
		method: 'POST',
		body: JSON.stringify({ period }),
	})) as TakeBreakResponse;

	return response;
};

export const getDepositPromotions = async () => {
	const response = (await remoteJSON('/users/promotions/deposit')) as GetDepositPromotionsResponse;

	return response;
};

export const getDepositRequestTypes = async (): Promise<DepositTypes> => {
	const response = (await remoteJSON('/users/deposit_request_types')) as DepositTypes;

	return response;
};

export const addDepositRequestTypeToUser = async (requestTypeId: number) => {
	if (requestTypeId === PaymentMethods.Card) return; // we cannot send CC though this method
	const response = await remoteJSON('/users/deposit_request_types', {
		method: 'POST',
		body: JSON.stringify({ deposit_request_type_id: requestTypeId }),
	});

	return response;
};

export const removeDepositRequestTypeFromUser = async (requestTypeId: number) => {
	if (requestTypeId === PaymentMethods.Card) return; // we cannot delete CC though this method
	const response = await remoteJSON('/users/deposit_request_types/', {
		method: 'DELETE',
		body: JSON.stringify({ deposit_request_type_id: requestTypeId }),
	});

	return response;
};

export const getWithdrawalRequestTypes = async (): Promise<WithdrawalTypes> => {
	const response = (await remoteJSON('/users/withdrawal_request_types')) as WithdrawalTypes;

	return response;
};

// US only
export const createPaysafeWithdrawRegistrationRequest = async (paymentTypeId: number) => {
	const response = (await remoteJSON('/withdraw/paysafe/register', {
		method: 'POST',
		body: JSON.stringify({
			deposit_request_type_id: paymentTypeId,
			return_url: `http://${window.location.host}/account/withdraw`,
			failed_url: `http://${window.location.host}/account/withdraw`,
		}),
	})) as CreatePaysafeWithdrawRegistrationResponse;

	return response;
};

export const createPaysafeWithdrawRequest = async (requestTypeId: number, amount: number, accountId: number) => {
	const response = (await remoteJSON('/withdraw/paysafe/create', {
		method: 'POST',
		body: JSON.stringify({ withdrawal_request_type_id: requestTypeId, amount, account_id: accountId }),
	})) as any;

	return response;
};

export const getCurrentTerms = async (): Promise<CurrentTermsResponse> => {
	return (await remoteJSON('/terms/current', { method: 'GET' })) as CurrentTermsResponse;
};

export const acceptTerms = async (version: string) => {
	return await remoteJSON('/users/accept_terms', {
		method: 'POST',
		body: JSON.stringify({ accept_terms: true, version: version }),
	});
};

export const createRegularPaysafeDepositRequest = async (paymentTypeId: number, amount: number) => {
	const response = (await remoteJSON('/deposit/paysafe/create', {
		method: 'POST',
		body: JSON.stringify({ deposit_request_type_id: paymentTypeId, amount: amount }),
	})) as CreatePaysafeDepositResponse;

	return response;
};

// For VipPreferred and Sightline payments
export const createSecuredPaysafeDepositRequest = async (paymentTypeId: number, amount: number) => {
	const response = (await remoteJSON('/deposit/paysafe/create', {
		method: 'POST',
		body: JSON.stringify({
			deposit_request_type_id: paymentTypeId,
			amount: amount,
			return_url: `http://${window.location.host}/betting/?modal=deposit`,
			failed_url: `http://${window.location.host}/betting/?modal=deposit`,
		}),
	})) as CreatePaysafeDepositResponse;

	return response;
};

export const processPaysafeDepositRequest = async (depositRequestId: number, paymentHandleToken: string) => {
	const response = remoteJSON(`/deposit/paysafe/${depositRequestId}/process`, {
		method: 'POST',
		body: JSON.stringify({ token: paymentHandleToken }),
	});

	return response;
};

export const getLimitsHistory = async (page: number | string = 1, from_date?: string, to_date?: string) => {
	const query = QueryString.stringify({ page, from_date, to_date }, { skipNulls: true });

	const response = (await remoteJSON(`/users/limits/history?${query}`)) as LimitsHistoryResponse;

	return response;
};

export const getClientSummary = async (client_id: number) => {
	const response = (await remoteJSON(`/users/client_summaries?client_id=${client_id}`)) as BonusesBalancesResponse;
	return response;
};

export const getRewards = async (client_id:"1"|"2") => {
	const response = (await remoteJSON(`/users/rewards?client_id=${client_id}`)) as RewardsResponse;
	return response;
}